import http from '../shared/HttpCommon';
import { Dispatch, SetStateAction } from 'react';
import { ISurvey } from '../shared/interfaces/ISurvey';
import { CanceledError } from 'axios';
import ITrendResponse from '../shared/interfaces/ITrendResponse';
import { ISurveyStandardEntry } from '../shared/interfaces/ISurveyStandardEntry';
import { ICustomSurveyEntry } from '../shared/interfaces/ICustomSurveyEntry';
import { ICustomSurveyQuestion } from '../shared/interfaces/ICustomSurveyQuestion';
import { ISurveysEntry } from '../shared/interfaces/ISurveysEntry';
import SurveyUtils from '../shared/SurveyUtils';
import { ITrendEntry } from '../shared/interfaces/ITrendEntry';
import { INpsEntry } from '../shared/interfaces/INpsEntry';
import { ISurveyStatistics } from '../shared/interfaces/ISurveyStatistics';
import { ISurveyParticipants, ISurveyResponsePage } from '../shared/interfaces/ISurveyResponsePage';

const getSurveyStatistics = async (surveyId: string, signal?: AbortSignal) => {
  try {
    const { data } = await http.get<ISurveyStatistics>(`/survey/${surveyId}/statistics`, {
      signal,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) console.error(error);
    return false;
  }
};

const getSurveyResponsePage = async (
  surveyId: string,
  params?: { limit?: number; after?: string },
  signal?: AbortSignal
) => {
  try {
    const { data } = await http.get<ISurveyResponsePage>(`/survey/${surveyId}/responses`, {
      params,
      signal,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) console.error(error);
    return false;
  }
};

const getSurveyParticipants = async (signal?: AbortSignal) => {
  try {
    const { data } = await http.get<{ participants: ISurveyParticipants[] }>(
      '/surveys/participants',
      { signal }
    );
    return data.participants;
  } catch (error) {
    if (!(error instanceof CanceledError)) console.error(error);
    return false;
  }
};

const getSurvey = async (loading: Dispatch<SetStateAction<boolean>>, signal: AbortSignal) => {
  try {
    loading(true);
    const { data } = await http.get<ISurveysEntry[]>('/surveys', { signal });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
  } finally {
    loading(false);
  }
};

export type SurveyTypes = ICustomSurveyEntry | ITrendEntry | INpsEntry;
const getSurveyDetails = async (
  survey: Partial<ISurveysEntry>,
  loading?: Dispatch<SetStateAction<boolean>>,
  signal?: AbortSignal
): Promise<SurveyTypes | undefined> => {
  if (!survey.id || !survey.type) return;
  return getSurveyPublic(survey.id, SurveyUtils.getEndpointByType(survey.type), loading, signal);
};

const getQuestions = async (
  surveyId: string,
  type: string,
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal
) => {
  try {
    loading(true);
    const { data } = await http.get<ICustomSurveyQuestion[]>(
      `/survey/${type.toLowerCase()}/${surveyId}/questions`,
      {
        signal,
      }
    );
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
  } finally {
    loading(false);
  }
};

const getSurveyPublic = async (
  surveyId: string,
  type: string,
  loading?: Dispatch<SetStateAction<boolean>>,
  signal?: AbortSignal
) => {
  try {
    if (loading) loading(true);
    const { data } = await http.get(`/survey/public/${type}/${surveyId}`, { signal });
    return data;
  } catch (error) {
    if (error instanceof CanceledError) {
    } else {
      console.error(error);
    }
  } finally {
    if (loading) loading(false);
  }
};

const saveSurveyPlatformClick = async (surveyId: string, type: string, url: string) => {
  try {
    const { data } = await http.post(`/survey/public/${type}/${surveyId}/platformclick`, {
      url,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const saveSurveyPlatformClickBeforeNavigation = async (
  surveyId: string,
  type: string,
  url: string,
  timeoutInMs = 800
) => {
  await Promise.race([
    saveSurveyPlatformClick(surveyId, type, url),
    new Promise((resolve) => setTimeout(resolve, timeoutInMs)),
  ]);
};

const getSurveyPlatformClickCount = async (surveyId: string) => {
  try {
    const { data } = await http.get<{ count: number }>(`/survey/${surveyId}/platformclicks`);
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return { count: 0 };
  }
};

const getNpsSurveyScore = async (
  surveyId: string,
  loading: Dispatch<SetStateAction<boolean>>,
  signal: AbortSignal
) => {
  try {
    loading(true);
    const { data } = await http.get(`/survey/nps/${surveyId}/score`, { signal });
    return data;
  } catch (error) {
    if (error instanceof CanceledError) {
    } else {
      console.error(error);
    }
  } finally {
    loading(false);
  }
};

const addNpsSurvey = async (NPSSurvey: ISurvey) => {
  try {
    const response = await http.post('/survey/nps', NPSSurvey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const archivedSurvey = async (surveyId: string, archived: boolean) => {
  try {
    const { data } = await http.patch(`/survey/${surveyId}/status`, { archived });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const saveSurveyNote = async (surveyId: string, note: string) => {
  try {
    const { data } = await http.patch(`/survey/${surveyId}/note`, { note });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const saveNpsSurveyAnswer = async (
  surveyId: string,
  answer: number,
  email: string,
  feedback: string,
  name: string,
  additionalData: Record<string, string | boolean>
) => {
  try {
    const { data } = await http.post(`/survey/public/nps/${surveyId}/answer`, {
      answer,
      email,
      feedback,
      name,
      ...additionalData,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateNpsSurveyAnswer = async (
  answerId: string,
  surveyId: string,
  answer: number,
  email: string,
  feedback: string,
  name: string,
  additionalData: Record<string, string | boolean>
) => {
  try {
    const { data } = await http.patch(`/survey/public/nps/${surveyId}/answer/${answerId}`, {
      answer,
      email,
      feedback,
      name,
      ...additionalData,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const saveTrendSurveyAnswer = async (
  surveyId: string,
  answer: string,
  feedback: string,
  additionalData: Record<string, string | boolean>
) => {
  try {
    const { data } = await http.post(`/survey/public/trend/${surveyId}/answer`, {
      answer,
      feedback,
      ...additionalData,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateTrendSurveyAnswer = async (
  answerId: string,
  surveyId: string,
  answer: string,
  feedback: string,
  additionalData: Record<string, string | boolean>
) => {
  try {
    const { data } = await http.patch(`/survey/public/trend/${surveyId}/answer/${answerId}`, {
      answer,
      feedback,
      ...additionalData,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateTrendSurvey = async (surveyId: string, survey: ISurvey) => {
  try {
    const response = await http.put(`/survey/trend/${surveyId}`, survey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateNpsSurvey = async (surveyId: string, survey: ISurvey) => {
  try {
    const response = await http.put(`/survey/nps/${surveyId}`, survey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateSurvey = async (surveyId: string, survey: ISurveyStandardEntry) => {
  try {
    const response = await http.put(`/survey/standard/${surveyId}`, survey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const addQuestion = async (surveyId: string, type: string, survey: ICustomSurveyQuestion) => {
  try {
    const response = await http.post(`/survey/${type.toLowerCase()}/${surveyId}/question`, survey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateCustomsurvey = async (surveyId: string, survey: ICustomSurveyEntry) => {
  try {
    const response = await http.put<ICustomSurveyEntry>(`/survey/customsurvey/${surveyId}`, survey);
    return response.data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const saveStandardSurveyAnswer = async (
  surveyId: string,
  answer: { questionId: string; answer: number; feedback: string }[]
) => {
  try {
    const { data } = await http.post(`/survey/public/standard/${surveyId}/answer`, {
      answers: answer,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const saveCustomSurveyAnswer = async (
  surveyId: string,
  answer: { questionId: string; answer: number; feedback: string }[]
) => {
  try {
    const { data } = await http.post(`/survey/public/customsurvey/${surveyId}/answer`, {
      answers: answer,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateStandardSurveyAnswer = async (
  answerId: string,
  surveyId: string,
  answer: { questionId: string; answer: number; feedback: string },
  additionalData: Record<string, string | boolean>
) => {
  try {
    const { data } = await http.patch(`/survey/public/standard/${surveyId}/answer/${answerId}`, {
      ...answer,
      ...additionalData,
    });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const getSurveyResponse = async (surveyId: string, signal: AbortSignal) => {
  try {
    const { data } = await http.get(`/survey/${surveyId}/answers`, { signal });
    return data;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const getTrendResponse = async (surveyId: string, signal: AbortSignal) => {
  try {
    const { data } = await http.get(`/survey/trend/${surveyId}/answers`, { signal });

    return data.surveyData.map((item: ITrendResponse) => {
      return {
        answers: item.answers,
        averageRating: item.averageRating,
        createdAt: item.createdAt,
        id: item.id,
        surveyId: item.surveyId,
        mood: item.mood,
        score: item.score,
        totalCount: item.totalCount,
      };
    });
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const addTrendSurvey = async (trendSurvey: ISurvey) => {
  try {
    const response = await http.post('/survey/trend', trendSurvey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const addSurvey = async (survey: ISurveyStandardEntry) => {
  try {
    const response = await http.post('/survey/standard', survey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const addCustomsurvey = async (survey: ICustomSurveyEntry) => {
  try {
    const response = await http.post('/survey/customsurvey', survey);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const deleteSurvey = async (surveyId: string) => {
  try {
    const response = await http.delete(`/survey/${surveyId}`);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const deleteQuestion = async (type: string, surveyId: string, questionId: string) => {
  try {
    const response = await http.delete(
      `/survey/${type.toLowerCase()}/${surveyId}/question/${questionId}`
    );
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const updateQuestion = async (
  type: string,
  surveyId: string,
  questionId: string,
  question: ICustomSurveyQuestion
) => {
  try {
    const response = await http.patch(
      `/survey/${type.toLowerCase()}/${surveyId}/question/${questionId}`,
      question
    );
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const resetSurvey = async (surveyId: string) => {
  try {
    const response = await http.patch(`/survey/${surveyId}/reset`);
    return response.status === 200;
  } catch (error) {
    if (!(error instanceof CanceledError)) {
      console.error(error);
    }
    return false;
  }
};

const SurveyService = {
  getSurveyStatistics,
  getSurveyResponsePage,
  getSurveyParticipants,
  getSurveyDetails,
  getSurvey,
  saveSurveyPlatformClick,
  saveSurveyPlatformClickBeforeNavigation,
  getSurveyPlatformClickCount,
  archivedSurvey,
  saveSurveyNote,
  saveNpsSurveyAnswer,
  updateNpsSurveyAnswer,
  updateNpsSurvey,
  addNpsSurvey,
  saveTrendSurveyAnswer,
  updateTrendSurveyAnswer,
  getSurveyResponse,
  getSurveyPublic,
  saveStandardSurveyAnswer,
  updateStandardSurveyAnswer,
  addSurvey,
  updateSurvey,
  addCustomsurvey,
  updateCustomsurvey,
  getTrendResponse,
  addTrendSurvey,
  updateTrendSurvey,
  deleteSurvey,
  resetSurvey,
  getNpsSurveyScore,
  addQuestion,
  getQuestions,
  deleteQuestion,
  updateQuestion,
  saveCustomSurveyAnswer,
};

export default SurveyService;
